import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { motion } from "framer-motion";
import { useTranslation } from 'react-i18next';
import { Container, Row, Col, Card } from "react-bootstrap";
import 'bootstrap/dist/css/bootstrap.min.css';

function DonationList() {
  const { i18n } = useTranslation();
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    const q = query(
      collection(db, "donations"),
      where("status", "==", "approved"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const donorsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setDonors(donorsData);
      
      const total = donorsData.reduce((sum, donor) => sum + (donor.amount || 0), 0);
      setTotalAmount(total);
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    
    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'th-TH', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      return new Date(timestamp).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'th-TH');
    } catch (error) {
      return "";
    }
  };

  return (
    <div style={{ paddingTop: '80px', minHeight: '100vh', background: 'linear-gradient(135deg, #e0f7fa 0%, #f0f9ff 50%, #e1f5fe 100%)' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        padding: '5rem 0 4rem',
        marginBottom: '3rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated decorative circles */}
        <motion.div
          style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            filter: 'blur(60px)'
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.15, 0.1]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          style={{
            position: 'absolute',
            bottom: '-80px',
            left: '-80px',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
            filter: 'blur(80px)'
          }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.08, 0.12, 0.08]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Floating emojis */}
        <motion.div
          style={{
            position: 'absolute',
            top: '15%',
            left: '10%',
            fontSize: '3rem',
            opacity: '0.15'
          }}
          animate={{ 
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{ 
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >💝</motion.div>
        <motion.div
          style={{
            position: 'absolute',
            top: '60%',
            right: '8%',
            fontSize: '2.5rem',
            opacity: '0.12'
          }}
          animate={{ 
            y: [0, -15, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >🤲</motion.div>
        <motion.div
          style={{
            position: 'absolute',
            bottom: '20%',
            left: '15%',
            fontSize: '2rem',
            opacity: '0.1'
          }}
          animate={{ 
            y: [0, -10, 0],
            rotate: [0, -15, 15, 0]
          }}
          transition={{ 
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >✨</motion.div>
        
        <Container style={{ position: 'relative' }}>
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              style={{
                fontSize: '5rem',
                marginBottom: '1rem'
              }}
              animate={{ 
                scale: [1, 1.15, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 2.5,
                repeat: Infinity,
                repeatDelay: 2
              }}
            >
              🙏
            </motion.div>
            
            <h1 style={{
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              fontWeight: '800',
              color: 'white',
              marginBottom: '1rem',
              textShadow: '0 2px 20px rgba(0,0,0,0.2)'
            }}>
              {i18n.language === 'en' ? 'Wall of Kindness' : 'กำแพงแห่งความดี'}
            </h1>
            <p style={{
              fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
              color: 'rgba(255, 255, 255, 0.95)',
              maxWidth: '700px',
              margin: '0 auto 2.5rem',
              lineHeight: '1.8'
            }}>
              {i18n.language === 'en'
                ? 'Every donation makes a difference. Thank you for being heroes who create positive change in the world.'
                : 'ทุกการบริจาคสร้างความเปลี่ยนแปลง ขอบคุณที่เป็นฮีโร่ผู้สร้างการเปลี่ยนแปลงที่ดีให้กับโลก'}
            </p>

            {/* Total Donations Card */}
            <motion.div
              style={{
                background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)',
                borderRadius: '24px',
                padding: '2.5rem',
                maxWidth: '500px',
                margin: '0 auto',
                boxShadow: '0 20px 25px -5px rgba(79,172,254,0.3), 0 10px 10px -5px rgba(79,172,254,0.15)',
                border: '2px solid rgba(255,255,255,0.6)',
                position: 'relative',
                overflow: 'hidden'
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ scale: 1.02, y: -5 }}
            >
              <motion.div
                style={{
                  position: 'absolute',
                  top: '-50px',
                  right: '-50px',
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.4)',
                }}
                animate={{
                  rotate: [0, 360]
                }}
                transition={{
                  duration: 15,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                marginBottom: '0.75rem',
                position: 'relative'
              }}>
                <motion.span
                  style={{ fontSize: '1.5rem' }}
                  animate={{
                    y: [0, -5, 0]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >💰</motion.span>
                <div style={{ fontSize: '1rem', color: '#00695c', fontWeight: '600' }}>
                  {i18n.language === 'en' ? 'Total Donations' : 'ยอดบริจาครวม'}
                </div>
              </div>
              <div style={{ 
                fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', 
                fontWeight: '800', 
                background: 'linear-gradient(135deg, #4fc3f7 0%, #0288d1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: '1.2',
                marginBottom: '1rem',
                position: 'relative'
              }}>
                ฿{totalAmount.toLocaleString('th-TH')}
              </div>
              <div style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: 'rgba(255, 255, 255, 0.7)',
                borderRadius: '20px',
                fontSize: '0.9rem', 
                color: '#00695c',
                fontWeight: '600',
                position: 'relative'
              }}>
                <span>👥</span>
                {i18n.language === 'en' 
                  ? `${donors.length} generous ${donors.length !== 1 ? 'donors' : 'donor'}`
                  : `${donors.length} ท่าน`}
              </div>
            </motion.div>
          </motion.div>
        </Container>
      </section>

      {/* Donors List */}
      <Container className="pb-5">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">
              {i18n.language === 'en' ? 'Loading donors...' : 'กำลังโหลดข้อมูล...'}
            </p>
          </div>
        ) : donors.length === 0 ? (
          <Card className="text-center shadow-sm" style={{ padding: '4rem 2rem' }}>
            <Card.Body>
              <h4 className="text-muted">
                {i18n.language === 'en' 
                  ? 'No donations yet. Be the first to donate!'
                  : 'ยังไม่มีผู้บริจาค เป็นคนแรกสิ!'}
              </h4>
            </Card.Body>
          </Card>
        ) : (
          <Row>
            {donors.map((donor, index) => {
              const avatarColors = [
                'linear-gradient(135deg, #4fc3f7 0%, #29b6f6 100%)',
                'linear-gradient(135deg, #03a9f4 0%, #0288d1 100%)',
                'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)',
                'linear-gradient(135deg, #4dd0e1 0%, #26c6da 100%)',
                'linear-gradient(135deg, #81d4fa 0%, #4fc3f7 100%)',
                'linear-gradient(135deg, #b3e5fc 0%, #81d4fa 100%)',
              ];
              const avatarBg = avatarColors[index % avatarColors.length];
              
              return (
                <Col xs={12} sm={6} md={6} lg={4} key={donor.id} className="mb-4">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    whileHover={{
                      y: -8,
                      boxShadow: '0 20px 30px rgba(79, 195, 247, 0.2)'
                    }}
                    style={{ position: 'relative' }}
                  >
                    {/* Sparkle Effect on Hover */}
                    <motion.div
                      style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        fontSize: '1.5rem',
                        zIndex: 1
                      }}
                      initial={{ scale: 0, rotate: 0 }}
                      whileHover={{
                        scale: [0, 1.2, 1],
                        rotate: [0, 180, 360],
                        transition: { duration: 0.6 }
                      }}
                    >
                      ✨
                    </motion.div>
                    <Card 
                      className="h-100"
                      style={{
                        borderRadius: '20px',
                        border: 'none',
                        background: 'white',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                        overflow: 'hidden',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.07)';
                      }}
                    >
                      {/* Top Accent */}
                      <div style={{
                        height: '6px',
                        background: avatarBg
                      }} />
                      
                      <Card.Body style={{ padding: '2rem 1.5rem' }}>
                        {/* Avatar */}
                        <div style={{
                          width: '70px',
                          height: '70px',
                          borderRadius: '50%',
                          background: avatarBg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 1.25rem',
                          fontSize: '1.75rem',
                          fontWeight: '700',
                          color: 'white',
                          boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                          border: '3px solid white'
                        }}>
                          💖
                        </div>

                        {/* Donor Name (Anonymous) */}
                        <h5 style={{
                          textAlign: 'center',
                          marginBottom: '0.75rem',
                          fontWeight: '600',
                          color: '#1a202c',
                          fontSize: '1.1rem'
                        }}>
                          {i18n.language === 'en' ? `Donor #${index + 1}` : `ผู้บริจาคท่านที่ ${index + 1}`}
                        </h5>

                        {/* Donation Type Badge */}
                        {donor.donationType && (
                          <div style={{
                            textAlign: 'center',
                            marginBottom: '0.75rem'
                          }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              background: donor.donationType === 'money' ? '#e0f2fe' : '#fef3c7',
                              color: donor.donationType === 'money' ? '#0369a1' : '#92400e'
                            }}>
                              {donor.donationType === 'money' 
                                ? (i18n.language === 'en' ? '💵 Money' : '💵 เงิน')
                                : (i18n.language === 'en' ? '📦 Items' : '📦 สิ่งของ')}
                            </span>
                          </div>
                        )}

                        {/* Amount or Description */}
                        {donor.amount ? (
                          <div style={{
                            textAlign: 'center',
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            marginBottom: '1rem'
                          }}>
                            ฿{donor.amount?.toLocaleString('th-TH')}
                          </div>
                        ) : donor.itemDescription && (
                          <div style={{
                            textAlign: 'center',
                            fontSize: '0.9rem',
                            color: '#64748b',
                            marginBottom: '1rem',
                            fontStyle: 'italic',
                            lineHeight: '1.5'
                          }}>
                            "{donor.itemDescription.length > 50 
                              ? donor.itemDescription.substring(0, 50) + '...' 
                              : donor.itemDescription}"
                          </div>
                        )}

                        {/* Date */}
                        <div style={{
                          textAlign: 'center',
                          fontSize: '0.8rem',
                          color: '#94a3b8',
                          marginBottom: '1rem'
                        }}>
                          {formatDate(donor.approvedAt || donor.createdAt)}
                        </div>

                        {/* Heart Icon */}
                        <div style={{
                          textAlign: 'center',
                          fontSize: '1.5rem'
                        }}>
                          💝
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              );
            })}
          </Row>
        )}
      </Container>
    </div>
  );
}

export default DonationList;
